import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { UserActions } from "../redux/UserActions";
import { moodColors, moodValues } from "../redux/constants";

import PageTitle from "./General/PageTitle";
import TableContainer from "./Table/TableContainer";
import TableHead from "./Table/TableHead";
import TableBody from "./Table/TableBody";
import ChartCard from "./General/ChartCard";

function Dashboard() {
  const userState = useSelector((state) => state.user);

  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [data, setData] = useState([]);

  const [filters, setFilters] = useState({});

  const tableOptions = {
    columns: [
      { name: "Person", filter: "name", isFiltering: false },
      { name: "Mood", filter: "mood", isFiltering: false },
      { name: "Priority", filter: "priority", isFiltering: false },
      { name: "Category", filter: "category", isFiltering: false },
      { name: "Department", filter: "department", isFiltering: false },
      { name: "Note", filter: "note", isFiltering: false },
      { name: "Date", filter: "date", isFiltering: false },
      { name: "Action" },
    ],
    rows: [],
  };

  useEffect(() => {
    let moods = userState.lastMoods;
    let moodCounter = {};
    tableOptions.rows = userState.lastMoods;
    setRows(moods);
    for (const mood of moods) {
      if (moodValues[mood.mood] in moodCounter)
        moodCounter[moodValues[mood.mood]] += 1;
      else moodCounter[moodValues[mood.mood]] = 1;
    }
    let moodData = [];
    for (const [key, value] of Object.entries(moodCounter)) {
      moodData.push({
        label: key,
        value: value,
        id: key,
        color: moodColors[key],
      });
    }
    setData(moodData);
  }, [userState.lastMoods]);

  useEffect(() => {
    let tempRows = userState.lastMoods;
    for (const [key, value] of Object.entries(filters)) {
      if (value === "") {
        let tempFilter = filters;
        delete tempFilter[key];
        setFilters(tempFilter);
      }
      if (key == "mood") {
        tempRows = tempRows.filter((person) =>
          moodValues[person.mood].toLowerCase().includes(value.toLowerCase())
        );
      }
      if (key == "name") {
        tempRows = tempRows.filter((person) =>
          [...person.user.firstName, ...person.user.lastName]
            .join("")
            .toLowerCase()
            .includes(value.toLowerCase().replaceAll(" ", ""))
        );
      }
      if (key == "department") {
        tempRows = tempRows.filter((person) =>
          [...person.user.department]
            .join("")
            .replaceAll(" ", "")
            .toLowerCase()
            .includes(value.toLowerCase().replaceAll(" ", ""))
        );
      }
      if (key == "category") {
        tempRows = tempRows.filter((person) =>
          [...person.categories]
            .join("")
            .toLowerCase()
            .includes(value.toLowerCase().replaceAll(" ", ""))
        );
      }
      if (key == "note") {
        tempRows = tempRows.filter((person) =>
          [...person.note]
            .join("")
            .toLowerCase()
            .includes(value.toLowerCase().replaceAll(" ", ""))
        );
      }
    }

    setRows(tempRows);
  }, [filters]);

  useEffect(() => {
    dispatch(UserActions.lastMoods({}));
    dispatch(UserActions.organization({}));
    dispatch(UserActions.users({}));
  }, []);
  return (
    <>
      <div className="flex flex-col w-full gap-y-4 p-6 bg-gray-50 dark:bg-gray-900">
        <DashboardChart
          data={data}
          filterUsers={(e) => setFilters({ ...filters, mood: e.id })}
        />
        <div className="flex bg-white w-full max-h-full overflow-y-hidden ">
          <TableContainer>
            <TableHead
              columns={tableOptions.columns}
              _handleFilter={(e, filter) =>
                setFilters({ ...filters, [filter]: e })
              }
            />
            <TableBody rows={rows} />
          </TableContainer>
        </div>
      </div>
    </>
  );
}

export default Dashboard;

function DashboardChart(props) {
  const userState = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [moodData, setMoodData] = useState([]);
  const [moodScore, setMoodScore] = useState(0);

  useEffect(() => {
    setMoodScore(parseInt(userState.moodScore?.moodScore));
    setMoodData([
      {
        id: "moodScore",
        data: [
          {
            x: "MoodScore",
            y: userState.moodScore.moodScore,
            color: "#4CAF50",
          },
        ],
      },
    ]);
  }, [userState.moodScore]);

  useEffect(() => {
    dispatch(
      UserActions.moodScore({
        organization: userState.user.organization,
      })
    );
  }, []);
  return (
    <div className="grid grid-cols-2 gap-x-2 h-96 items-center rounded p-2">
      <ChartCard className="flex items-center rounded" title="Latest Moods">
        <ResponsivePie
          data={props.data}
          margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
          innerRadius={0.5}
          padAngle={2}
          cornerRadius={5}
          colors={{ datum: "data.color" }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
          activeOuterRadiusOffset={15}
          onClick={(e) => {
            props.filterUsers(e);
          }}
        />
      </ChartCard>
      <ChartCard
        className="flex flex-col items-center bg-white rounded"
        title="Wellbeing Score"
      >
        <ResponsiveRadialBar
          data={moodData}
          margin={{ top: 40, right: 80, bottom: 0, left: 80 }}
          colors={{ datum: "data.color" }}
          maxValue={100}
          valueFormat=">-.2f"
          startAngle={-135}
          endAngle={135}
          innerRadius={0.7}
          padding={0.15}
          cornerRadius={20}
          enableRadialGrid={false}
          enableLabels={false}
          radialAxisStart={false}
          circularAxisOuter={false}
          isInteractive={false}
          enableTracks={false}
        />
        <label className="w-full text-center py-2 font-bold text-xl">
          {Math.ceil(moodScore) + "%"}
        </label>
      </ChartCard>
    </div>
  );
}
